// MockModeContext — dev-only toggle between mock and live data.
// In production (VITE_APP_ENV !== 'development') isMock is always false
// and toggle is a no-op so no dead code reaches prod bundles.

import { createContext, useContext, useState, type ReactNode } from 'react';

interface MockModeContextValue {
  isMock: boolean;
  toggle: () => void;
  /** Explicitly choose the data source (dev builds only; a no-op in production). */
  setMock: (next: boolean) => void;
}

const MockModeContext = createContext<MockModeContextValue>({
  isMock: false,
  toggle: () => {},
  setMock: () => {},
});

const IS_DEV = (import.meta.env['VITE_APP_ENV'] as string | undefined) === 'development';
const LS_KEY = 'vigilo_mock_mode';

function readInitial(): boolean {
  if (!IS_DEV) return false;
  const stored = localStorage.getItem(LS_KEY);
  // Default ON in dev when key has never been set — new engineers see rich data immediately.
  return stored === null ? true : stored === 'true';
}

export function MockModeProvider({ children }: { children: ReactNode }) {
  const [isMock, setIsMock] = useState<boolean>(readInitial);

  function toggle() {
    if (!IS_DEV) return;
    setIsMock(prev => {
      const next = !prev;
      localStorage.setItem(LS_KEY, String(next));
      return next;
    });
  }

  function setMock(next: boolean) {
    if (!IS_DEV) return;
    localStorage.setItem(LS_KEY, String(next));
    setIsMock(next);
  }

  return (
    <MockModeContext.Provider value={{ isMock, toggle, setMock }}>
      {children}
    </MockModeContext.Provider>
  );
}

export function useMockMode(): MockModeContextValue {
  return useContext(MockModeContext);
}
